/**
 * 管理员控制台页面
 * 整合所有管理功能的入口
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  Package,
  Users,
  Database,
  Shield,
  BarChart3,
  Gift,
} from 'lucide-react';

export function AdminPage() {
  const navigate = useNavigate();

  const adminFeatures = [
    {
      title: 'APP版本管理',
      description: '管理Android/iOS应用的在线升级版本',
      icon: Package,
      path: '/app-version',
      color: 'text-amber-500',
      bgColor: 'bg-amber-50 dark:bg-amber-950',
      badge: '新功能',
    },
    {
      title: '邀请页面配置',
      description: '管理邀请页面的海报图片和展示设置',
      icon: Gift,
      path: '/admin/invitation-page',
      color: 'text-rose-500',
      bgColor: 'bg-rose-50 dark:bg-rose-950',
      badge: '新功能',
    },
    {
      title: '用户管理',
      description: '管理用户账号、权限和状态',
      icon: Users,
      path: '/admin/users',
      color: 'text-orange-500',
      bgColor: 'bg-orange-50 dark:bg-orange-950',
    },
    {
      title: '订阅统计',
      description: '查看工具订阅数据和使用统计',
      icon: BarChart3,
      path: '/admin/analytics',
      color: 'text-pink-500',
      bgColor: 'bg-pink-50 dark:bg-pink-950',
      badge: '开发中',
      disabled: true,
    },
    {
      title: '系统配置',
      description: '配置系统参数、数据库和安全设置',
      icon: Settings,
      path: '/admin/system',
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-50 dark:bg-indigo-950',
      badge: '开发中',
      disabled: true,
    },
    {
      title: '数据库管理',
      description: '数据库备份、恢复和维护',
      icon: Database,
      path: '/admin/database',
      color: 'text-cyan-500',
      bgColor: 'bg-cyan-50 dark:bg-cyan-950',
      badge: '开发中',
      disabled: true,
    },
    {
      title: '审计日志',
      description: '查看系统操作日志和安全审计',
      icon: Shield,
      path: '/admin/audit',
      color: 'text-red-500',
      bgColor: 'bg-red-50 dark:bg-red-950',
      badge: '开发中',
      disabled: true,
    },
  ];

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">管理员控制台</h1>
        <p className="text-gray-600 dark:text-gray-400">
          集中管理系统的所有功能
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminFeatures.map((feature) => {
          const Icon = feature.icon;
          return (
            <Card 
              key={feature.path}
              className={`hover:shadow-lg transition-shadow ${feature.disabled ? 'opacity-60' : 'cursor-pointer'}`}
              onClick={() => !feature.disabled && navigate(feature.path)}
            >
              <CardHeader>
                <div className="flex items-start justify-between mb-3">
                  <div className={`p-3 rounded-lg ${feature.bgColor}`}>
                    <Icon className={`h-6 w-6 ${feature.color}`} />
                  </div>
                  {feature.badge && (
                    <span className={`text-xs px-2 py-1 rounded ${
                      feature.badge === '新功能' 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                    }`}>
                      {feature.badge}
                    </span>
                  )}
                </div>
                <CardTitle className="text-xl">{feature.title}</CardTitle>
                <CardDescription className="min-h-[3rem]">
                  {feature.description}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button 
                  variant={feature.disabled ? 'ghost' : 'default'}
                  className="w-full"
                  disabled={feature.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!feature.disabled) {
                      navigate(feature.path);
                    }
                  }}
                >
                  {feature.disabled ? '即将推出' : '进入管理'}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* 快速统计卡片 */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>MCP服务器</CardDescription>
            <CardTitle className="text-2xl">--</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>已发布工具</CardDescription>
            <CardTitle className="text-2xl">--</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>注册用户</CardDescription>
            <CardTitle className="text-2xl">--</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>总订阅数</CardDescription>
            <CardTitle className="text-2xl">--</CardTitle>
          </CardHeader>
        </Card>
      </div>
    </div>
  );
}

